/**
 * A session the device still holds while the app counts itself signed out.
 *
 * Clerk keeps the session on its client (the browser's cookie, the phone's
 * token cache), and the app reads "signed in" from Clerk's ACTIVE session. The
 * two can part: a sign-out in another tab, a session Clerk loaded as pending,
 * a launch whose client load failed. The sign-in and sign-up screens then show,
 * and Clerk refuses every new attempt with "signed in" — the person sees
 * "You're already signed in" on a screen that just told them they were not.
 *
 * The person on those screens asked to sign in (or up) now, as whoever they
 * type: the leftover session is signed out and the attempt runs once more.
 * Every way in — email sign-in, email sign-up, the password-reset code, the
 * social buttons on the web and on the phone — goes through
 * `retryAfterStaleSession`, so none of them can strand the person.
 */

/** Clerk's codes for "this client already holds a session". */
const STALE_SESSION_CODES = new Set([
  'session_exists',
  'signed_in',
  'identifier_already_signed_in',
]);

export function isStaleSession(error: unknown): boolean {
  const errors = (error as { errors?: { code?: string }[] } | null)?.errors;
  return Array.isArray(errors) && errors.some((e) => !!e.code && STALE_SESSION_CODES.has(e.code));
}

/**
 * Runs `attempt`; when Clerk refuses it for a leftover session, signs that
 * session out and runs it once more. Any other failure — or a second refusal —
 * is the caller's to report.
 */
export async function retryAfterStaleSession<T>(
  attempt: () => Promise<T>,
  signOut: () => Promise<unknown>
): Promise<T> {
  try {
    return await attempt();
  } catch (error) {
    if (!isStaleSession(error)) throw error;
    await signOut();
    return attempt();
  }
}
